<script>
import Icon from 'next/icon/Icon.vue';
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';

export default {
  name: 'MessagePreview',
  components: {
    Icon,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { getPlainText } = useMessageFormatter();
    return {
      getPlainText,
    };
  },
  computed: {
    messageByAgent() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.OUTGOING;
    },
    statusIcon() {
      const status = (this.message.status || '').toLowerCase();
      if (status === 'progress') return 'i-lucide-clock';
      if (status === 'read' || status === 'delivered') return 'i-lucide-check-check';
      return 'i-lucide-check';
    },
    statusColor() {
      const status = (this.message.status || '').toLowerCase();
      if (status === 'read') return 'text-[#53bdeb]';
      return 'text-n-slate-10';
    },
    isMessageAnActivity() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.ACTIVITY;
    },
    isMessagePrivate() {
      const { private: isPrivate } = this.message;
      return isPrivate;
    },
    parsedLastMessage() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      return this.getPlainText(subject || this.message.content);
    },
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments || [];
      return fileType;
    },
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType];
    },
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    isMessageSticker() {
      const msg = this.message || {};
      const attrs = msg.content_attributes || msg.contentAttributes || {};
      const contentType = msg.content_type || msg.contentType;
      const attachments = msg.attachments || [];
      const firstAttachment = attachments[0] || {};
      const fileType = (firstAttachment.file_type || firstAttachment.fileType || '').toLowerCase();
      const ext = (firstAttachment.extension || '').toLowerCase();
      const mime = (firstAttachment.content_type || firstAttachment.contentType || '').toLowerCase();
      const url = (firstAttachment.data_url || firstAttachment.dataUrl || firstAttachment.file_url || firstAttachment.fileUrl || '').toLowerCase();
      const filename = (firstAttachment.title || firstAttachment.filename || '').toLowerCase();

      return (
        contentType === 'sticker' ||
        attrs.is_sticker === true ||
        attrs.isSticker === true ||
        attrs.is_sticker === 'true' ||
        attrs.isSticker === 'true' ||
        fileType === 'sticker' ||
        ext === 'webp' ||
        mime.includes('webp') ||
        url.includes('.webp') ||
        filename.includes('sticker') ||
        filename.includes('.webp')
      );
    },
    isMessageAudio() {
      const msg = this.message || {};
      const contentType = msg.content_type || msg.contentType;
      const attachments = msg.attachments || [];
      const firstAttachment = attachments[0] || {};
      const fileType = (firstAttachment.file_type || firstAttachment.fileType || this.lastMessageFileType || '').toLowerCase();

      return (
        contentType === 'audio' ||
        contentType === 'voice_call' ||
        fileType === 'audio' ||
        fileType === 'voice_call'
      );
    },
    isMessageDeleted() {
      const { content_attributes: attrs, content } = this.message || {};
      return (
        attrs?.is_deleted === true ||
        attrs?.deleted === true ||
        attrs?.isDeleted === true ||
        content === 'Mensagem apagada' ||
        content === 'Esta mensagem foi apagada'
      );
    },
  },
};
</script>

<template>
  <div class="overflow-hidden text-ellipsis whitespace-nowrap">
    <template v-if="showMessageType">
      <fluent-icon
        v-if="isMessagePrivate"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="lock-closed"
      />
      <span
        v-else-if="messageByAgent"
        class="inline-flex items-center -mt-0.5 align-middle me-1"
        :class="statusColor"
      >
        <Icon
          :icon="statusIcon"
          class="size-3.5"
        />
      </span>
      <fluent-icon
        v-else-if="isMessageAnActivity"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="info"
      />
    </template>
    <span v-if="isMessageDeleted" class="inline-flex items-center gap-1 italic text-slate-400">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="size-3.5 inline-block shrink-0 -mt-0.5 opacity-80"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </svg>
      <span>Mensagem apagada</span>
    </span>
    <span v-else-if="isMessageSticker">
      ✨ Figurinha
    </span>
    <span v-else-if="isMessageAudio">
      🎤 Mensagem de áudio
    </span>
    <span v-else-if="message.content">
      {{ parsedLastMessage }}
    </span>
    <span v-else-if="message.attachments && message.attachments.length">
      <fluent-icon
        v-if="attachmentIcon && showMessageType"
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11"
        :icon="attachmentIcon"
      />
      {{ $t(`${attachmentMessageContent}`) }}
    </span>
    <span v-else>
      {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
    </span>
  </div>
</template>
